import React, { useEffect, useState } from 'react';
import * as Dialog from "@radix-ui/react-dialog";
import { X } from 'lucide-react';
import { Project } from '../models/Project';
import { isEmpty } from '../lib/Helper';

type ProjectEditorProps = {
    trigger: React.ReactNode;
    createProject: (project: Project) => void;
    existingProject?: Project;
    updateProject?: (project: Project) => void;
};

const ProjectEditor = ({ trigger, existingProject, createProject, updateProject}: ProjectEditorProps) => {

    const [isDialogOpen, setDialogOpen] = useState(false);
    const [project, setProject] = useState<Project>({
        projectID: "",
        title: ""
    });

    const onProjectIDChange = (e: React.FormEvent<HTMLInputElement>) => {  
        setProject({
            ...project,
            projectID: e.currentTarget.value
        });
    }

    const onTitleChange = (e: React.FormEvent<HTMLInputElement>) => {
        setProject({
            ...project,
            title: e.currentTarget.value
        });
    }

    const onDialogClose = () => {
        setDialogOpen(false);
    }

    const isSaveDisabled = () => {
        return isEmpty(project.projectID) || isEmpty(project.title);
    }

    const onSave = () => {
        if (existingProject && updateProject) {
            updateProject(project);
        } else {
            createProject(project);
        }

        setProject({
            projectID: "",
            title: ""
        });
        setDialogOpen(false);
    }

    useEffect(() => {
        if (existingProject) {
            setProject(existingProject);
        }
    }, [existingProject]);

    const closeBtnCls = 'py-2.5 px-5 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10';
    const closeIconCls = 'absolute right-3.5 top-5 inline-flex size-[25px] appearance-none items-center justify-center focus:outline-none text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto';
    const saveBtnCls = 'py-2.5 px-5 text-white bg-primary-blue hover:bg-blue-800 focus:outline-none font-medium rounded-lg text-sm text-center disabled:bg-gray-200';

    const labelClasses = "block text-left tracking-wide text-gray-700 text-xs font-bold mb-2";
    const inputClasses = "appearance-none block w-full text-gray-700 border border-gray-200 rounded py-2 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500";
    const isPhantom = existingProject === undefined;

    return (
        <Dialog.Root open={isDialogOpen} onOpenChange={setDialogOpen}>
            <Dialog.Trigger asChild>
                {trigger}
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-blackA6 data-[state=open]:animate-overlayShow" />
                <Dialog.Content onInteractOutside={(e) => {e.preventDefault();}} className="overflow-y-auto fixed left-1/2 top-1/3 w-[90vw] max-w-[850px] -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none data-[state=open]:animate-contentShow">
                    <Dialog.Title className='text-xl font-semibold text-gray-900 my-4'>
                        {isPhantom ? <span>Add new project</span> : <span>Update project</span>}
                    </Dialog.Title>
                    <Dialog.Description />

                    <div className="px-2 my-2 flex flex-wrap">
                        <div className="flex-1">
                            <div className='mb-4'>
                                <label className={labelClasses}>Project ID</label>
                                <input className={inputClasses} value={project.projectID} onChange={onProjectIDChange} disabled={!isPhantom}/>
                            </div>
                            <div className='mb-4'>
                                <label className={labelClasses}>Title</label>
                                <input className={inputClasses} value={project.title} onChange={onTitleChange} />
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between px-2 py-4 md:py-5 border-t border-gray-200 rounded-b">
                        <button type="button" className={closeBtnCls} onClick={onDialogClose}>Close</button>
                        <button type="button" className={saveBtnCls} disabled={isSaveDisabled()} onClick={onSave}>Save</button>
                    </div>

                    <Dialog.Close asChild>
                        <button className={closeIconCls} aria-label="Close"><X /></button>
                    </Dialog.Close>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    )
}

export default ProjectEditor;